import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { convertDateTime } from "../../utils/helpers";

@Injectable({
  providedIn: 'root',
})
export class DateTimeService {
  private readonly datePipe: DatePipe;

  private readonly numberOfMillisecondsPerDay = 86400000;

  constructor(private readonly translate: TranslateService) {
    this.datePipe = new DatePipe('da-DK');
  }

  public formatDate(date: Date){
    let month;
    let day;

    if (date.getMonth()+1 < 10){
      month = "0" + (date.getMonth()+1);
    } else {
      month = date.getMonth()+1;
    }

    if (date.getDate() < 10){
      day = "0" + (date.getDate());
    } else {
      day = date.getDate();
    }

    return (day + "-" + month + "-" + date.getFullYear());
  }

  public convertDanishDateToJsDate(date: string)
  {
    let dateParts = date.split("/");
    let newDateString = dateParts[1] + "/" + dateParts[0] + "/" + dateParts[2];

    return Date.parse(newDateString); 
  }

  public convertTimestampToDate(timestamp: Date | undefined): string | undefined {
    return timestamp && this.datePipe.transform(timestamp, "dd-MM-yyyy") as string | undefined;
  }
  public convertNullableDisplayDateToDate(timestamp?: string ): Date | undefined {
    if (timestamp === undefined || timestamp === null){
      return undefined;
    }
    return this.convertDisplayDateToDate(timestamp);
  }
  public convertDisplayDateToDate(timestamp: string ): Date { // Convert dd-MM-yyyy string to date
    const [day, month, year] = timestamp.split("-").map(Number);

    return new Date(year, month - 1, day);
  }

  public convertTimestampToShortDate(timestamp: Date | undefined): string | undefined {
    return timestamp && this.datePipe.transform(timestamp, "d.LLL y") as string | undefined;
  }
  
  public convertTimestampToLongDate(timestamp: Date | undefined): string | undefined {
    return timestamp && this.datePipe.transform(timestamp, "d. LLLL y") as string | undefined;
  }

  public convertTimestampToTime(timestamp: Date | undefined): string | undefined {
    return timestamp && this.datePipe.transform(timestamp, "HH:mm") as string | undefined;
  }
  
  public convertTimeOnlyToTime(time: string | undefined): string | undefined {
    return time?.slice(0, 5).replace('.', ':');
  }
  public datePipeTransformDate(date: Date, format: string){
    return this.datePipe.transform(date, format)
  }

  public transformDateTime(date: Date){
    return this.datePipe.transform(date, "dd-MM-yyyy HH.mm")
  }

  public datePipeTransformDateTime(date: Date){
    return this.datePipe.transform(date, "dd-LL-yyyy HH:mm:ss")
  }

  public datePipeTransformDateTimeWithoutSecond(date: Date){
    return this.datePipe.transform(date, "dd-LL-yyyy HH:mm")
  }

  public datePipeTransformDateOnly(date?: Date){
    return this.datePipe.transform(date, "dd-LL-yyyy")
  }

  public datePipeTransformShortYearDateOnly(date?: Date){
    return this.datePipe.transform(date, "dd-LL-yy")
  }

  public getAge(birthdate: Date) {
    const castBirthDate = new Date(birthdate);
    const today = new Date()
    let age = today.getFullYear() - castBirthDate.getFullYear();// age one start at year 1
    const m = today.getMonth() - castBirthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < castBirthDate.getDate())) {
      age--;
    }
    return age;
  }

  public datesEqual(firstdate?: Date, secondDate?: Date)
  {
    return this.datePipeTransformDateOnly(firstdate) == this.datePipeTransformDateOnly(secondDate);
  }

  public datePipeTransformDateWithoutSeconds(date?: Date){
    return this.datePipe.transform(date, "dd. MMMM yyyy HH:mm")
  }

  getRemainingDaysTilSpecificDate(pastDate: string, destinationDate: string) {
    const differenceInMilliseconds = convertDateTime(pastDate).getTime() -
        convertDateTime(destinationDate).getTime();
    return Math.abs(Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24)));
  }

  getRemainingDaysTilNow(pastDate: string) {
    const differenceInMilliseconds = convertDateTime(pastDate).getTime() - new Date().getTime();
    return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24));
  }

  getFormattedDate(inputDate?: string, format?: string){
    if(inputDate == null)
    {
      return ""
    }
    const date = new Date(inputDate);
    return this.datePipe.transform(date,format ?? "dd. MMMM yyyy");
  }

  getFormattedYearOfDate(inputDate?: string) {
    return this.datePipe.transform(inputDate, 'yyyy');
  }

  getDateNearestToday(dates: Date[]) {
    const today = new Date();
    let nearestDate = today;
    let nearestDiff = Number.MAX_VALUE;

    for (const date of dates) {
      const diff = date.getTime() - today.getTime();
      if (diff > -this.numberOfMillisecondsPerDay && diff < nearestDiff) {
        nearestDiff = diff;
        nearestDate = date;
      }
    }

    return nearestDate;
  }
  public today() : Date {
    const today = new Date();
    today.setHours(0,0,0,0);
    return today;
  }
}
